import { useAuthStore } from './authStore';
import { initializeHeldEditingSessionReleases } from './heldEditingSessions';
import { initializePreLogoutPeers } from './preLogoutPeers';

let internalAuthChannel: BroadcastChannel | null = null;
let isInitialized = false;

// Getter function for authChannel (avoids export of mutable let)
export function getAuthChannel(): BroadcastChannel | null {
  return internalAuthChannel;
}

// Initialize auth sync ONLY when explicitly called
export function initializeAuthSync() {
  if (isInitialized) {
    return;
  }

  isInitialized = true;
  internalAuthChannel = new BroadcastChannel('helios-auth-channel');

  // BUG-146069 — let this tab answer ANOTHER tab's sign-out by releasing what it
  // holds (the steering editor's document lock) while the browser is still
  // authenticated. It has to happen before the signing-out tab's logout POST, so
  // the `LOGOUT` case below is far too late: by then the shared cookie jar is
  // empty and a release would 401. Wired here because every tab runs
  // initializeAuthSync() on mount; on its own channel, so this handshake stays
  // out of the LOGIN/LOGOUT protocol. A tab holding nothing stays silent.
  initializePreLogoutPeers();

  // BUG-146069-005 — release the editing sessions this BROWSER recorded, which is the half the
  // in-page registry cannot cover: its `Map` dies with the page, while the backend session and
  // its exclusive lock deliberately do not. Wired here for the same reason as the peer handshake
  // and one more: this runs on every page load whatever route it landed on, which is exactly what
  // "nav away → reload → sign out" needs. Registering the task is all this does; it reads the
  // record at sign-out time.
  initializeHeldEditingSessionReleases();

  // Listen for auth events from other tabs
  internalAuthChannel.onmessage = (event) => {
    const { type, payload } = event.data;

    switch (type) {
      case 'LOGIN':
        if (payload?.user) {
          useAuthStore.getState().setUser(payload.user);
        }
        break;

      case 'LOGOUT':
        useAuthStore.getState().clearAuthState();
        useAuthStore.getState().setUser(null);
        // Dispatch event for navigation handling in App.tsx
        window.dispatchEvent(new CustomEvent('auth:logout'));
        break;

      case 'TOKEN_REFRESH':
        useAuthStore.getState().loadUserFromToken();
        break;

      default:
        // Ignore unknown message types
        break;
    }
  };

  // Subscribe to store changes and broadcast to other tabs
  const unsubscribe = useAuthStore.subscribe(
    (state) => state.user,
    (user, previousUser) => {
      if (user?.id !== previousUser?.id) {
        internalAuthChannel?.postMessage({
          type: user ? 'LOGIN' : 'LOGOUT',
          payload: { user },
          timestamp: Date.now(),
        });
      }
    }
  );

  // Listen for 401 unauthorized from axios interceptor
  const handleUnauthorized = () => {
    useAuthStore.getState().setUser(null);
    internalAuthChannel?.postMessage({
      type: 'LOGOUT',
      timestamp: Date.now(),
    });
  };

  window.addEventListener('auth:unauthorized', handleUnauthorized);

  // Cleanup on beforeunload
  window.addEventListener('beforeunload', () => {
    unsubscribe();
    window.removeEventListener('auth:unauthorized', handleUnauthorized);
    internalAuthChannel?.close();
  });
}
